package games.polyclub.website.frontend.store.pages.logged.inventory

import games.polyclub.frontend.lib.messagerenderer.m
import games.polyclub.frontend.lib.react.bootstrap.components.BAlert
import games.polyclub.frontend.lib.react.bootstrap.components.Card
import games.polyclub.frontend.lib.react.bootstrap.custom.oldcomponents.CountdownProgressBar
import games.polyclub.frontend.lib.react.simplified.SFC
import games.polyclub.frontend.lib.react.simplified.tags.*
import games.polyclub.frontend.lib.react.utils.classSet
import games.polyclub.frontend.lib.react.utils.classes
import games.polyclub.frontend.lib.react.utils.useEffectOnceAsync
import games.polyclub.message.unaryMinus
import games.polyclub.service.payment.api.id.ProductId
import games.polyclub.service.store.api.ConsumableInventory
import games.polyclub.service.store.api.Product.Type.*
import games.polyclub.tool.functional.retrying
import games.polyclub.website.frontend.store.StoreBff
import games.polyclub.website.frontend.store.appScope
import games.polyclub.website.frontend.store.components.Loading
import games.polyclub.website.frontend.store.context.addBreadcrumb
import games.polyclub.website.frontend.store.hooks.useAuth
import games.polyclub.website.frontend.store.layout.userarea.UserAreaLayout
import games.polyclub.website.frontend.store.sitemap.Icon
import games.polyclub.website.frontend.store.sitemap.LoggedSitemap
import js.core.jso
import kotlinx.datetime.Clock
import react.Props
import react.useEffectOnce
import react.useState
import web.cssom.Overflow
import web.cssom.Position
import web.cssom.px
import web.timers.clearInterval
import web.timers.setInterval
import kotlin.time.Duration.Companion.seconds

private enum class ItemStatus {
    CONSUMED,
    AVAILABLE,
    PAUSED,
    ACTIVE,
    END_NOT_NOTIFIED
}

val InventoryPage = SFC<Props> { _ ->
    val authCtx = useAuth()
    val profile = authCtx.auth!!.profile
    val (inventory, setInventory) = useState<ConsumableInventory?>(null)
    val (now, setNow) = useState(Clock.System.now())
    val (focusing, setFocusing) = useState<ProductId?>(null)
    appScope.useEffectOnceAsync {
        retrying {
            setInventory(StoreBff.loadInventory())
        }
    }
    useEffectOnce {
        val interval = setInterval(1.seconds) {
            setNow(Clock.System.now())
        }
        cleanup {
            clearInterval(interval)
        }
    }

    UserAreaLayout {
        addBreadcrumb(LoggedSitemap.INVENTORY.pathPart, -"Inventário")
        val item = focusing?.let { inventory?.get(it) }
        if (item != null) {
            InventoryFocusPage {
                this.profile = profile
                this.item = item
                this.now = now
                this.goBack = { setFocusing(null) }
            }
            return@UserAreaLayout
        }
        Card {
            title = "Inventário"
            if (inventory == null) {
                Loading()
                return@Card
            }
            if (inventory.isEmpty()) {
                BAlert {
                    isDismissible = false
                    +"Seu inventário está vazio. Que tal comprar alguma coisa para encher ele? 🥰"
                }
                return@Card
            }
            div("d-flex flex-row poly-inventory") {
                inventory.asSequence().filterNot { it.productId.isSpecial }.forEach { item ->
                    val activePeriod = item.timer.activePeriod()
                    val available = item.amount.available
                    val status = when (item.productType) {
                        COIN, CONSUMABLE -> when {
                            available <= 0 -> ItemStatus.CONSUMED
                            else -> ItemStatus.AVAILABLE
                        }
                        TEMPORAL -> {
                            when {
                                activePeriod?.isPaused() == true -> ItemStatus.PAUSED
                                activePeriod != null -> ItemStatus.ACTIVE
                                item.timerEvents?.nextPendingPeriod() != null -> ItemStatus.AVAILABLE
                                item.timerEvents?.periodsEndedButNotNotified() != null -> ItemStatus.END_NOT_NOTIFIED
                                else -> ItemStatus.CONSUMED
                            }
                        }
                    }
                    val statusColor = when (status) {
                        ItemStatus.CONSUMED -> "gray"
                        ItemStatus.AVAILABLE -> "success"
                        ItemStatus.PAUSED -> "warning"
                        ItemStatus.ACTIVE -> "primary"
                        ItemStatus.END_NOT_NOTIFIED -> "secondary"
                    }
                    div("p-2 position-relative border border-$statusColor text-center rounded bg-light poly-item m-1") {
                        onClick = {
                            setFocusing(item.productId)
                        }
                        div("ribbon-wrapper") {
                            div("ribbon bg-$statusColor") {
                                small {
                                    +when (status) {
                                        ItemStatus.CONSUMED, ItemStatus.END_NOT_NOTIFIED -> "Consumido"
                                        ItemStatus.AVAILABLE -> "Disponível"
                                        ItemStatus.PAUSED -> "Pausado"
                                        ItemStatus.ACTIVE -> "Ativo"
                                    }
                                }
                            }
                        }
                        div("font-weight-bold") {
                            classSet += if (available <= 0) {
                                "text-danger"
                            } else {
                                "text-$statusColor"
                            }
                            style = jso {
                                position = Position.absolute
                                top = 0.px
                                left = 5.px
                            }
                            +available.toString()
                        }
                        div {
                            style = jso {
                                height = 100.px
                                maxHeight = 100.px
                                overflow = Overflow.hidden
                            }
                            div {
                                when (item.productId) {
                                    ProductId.ALL_OF_FABRIC_ACCESS_PASS -> Icon.src("/img/aof7/icon32x32.png", 64.px, 64.px)
                                    ProductId.SKYFACTORY_ACCESS_PASS -> Icon.src("/img/skyfactory/icon.png", 64.px, 64.px)
                                    else -> Icon.fas("fa-cog")
                                }.apply { renderIcon() }
                            }
                            h6 {
                                m(item.name)
                            }
                        }
                        div {
                            style = jso {
                                height = 74.px
                                maxHeight = 74.px
                                overflow = Overflow.hidden
                            }

                            if (activePeriod != null) {
                                CountdownProgressBar::class {
                                    this.classes = "my-1"
                                    this.min = activePeriod.started!!.at
                                    this.max = activePeriod.predictedEnd(ignoreCurrentPause = true) ?: now
                                    showClock = true
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}
